import type { CombineLatestInternal } from '../core/entities';
import type { SimEvent } from '../core/events';
import { isMatter, isTuple } from '../core/packet';
import type { MaterialTuple, Matter } from '../core/packet';
import { peekPort, portHasRoom, singleOutput, tryPushToPort, tryTakeFromPort } from '../core/port';
import type { Port } from '../core/port';
import { drainOutput } from './shared';
import type { MachineHandler } from './registry';

/** Round-robin from wherever the last emission left off, so a fast input cannot starve a slow one
 * out of ever refreshing its remembered value — which would quietly turn this back into `zip`. */
function nextReadyPort(inputs: Port[], from: number): number {
  for (let offset = 0; offset < inputs.length; offset++) {
    const index = (from + offset) % inputs.length;
    if (peekPort(inputs[index]) !== undefined) return index;
  }
  return -1;
}

function flatten(materials: Matter[]): MaterialTuple {
  return materials.flatMap((material) => (isTuple(material) ? [...material] : [material]));
}

function isComplete(latest: (Matter | null)[]): latest is Matter[] {
  return latest.every((material) => material !== null);
}

function resetOnFreshSubscription(internal: CombineLatestInternal, subscribed: boolean): void {
  if (subscribed && !internal.wasSubscribed) {
    internal.latest = internal.latest.map(() => null);
    internal.nextPort = 0;
  }
  internal.wasSubscribed = subscribed;
}

/**
 * The join that consumes nothing. Every arrival replaces that input's remembered material and emits
 * the whole set — so the same carbon pairs with every iron that follows it, matter from nothing, and
 * that is exactly the lesson: `zip` spends both halves, `combineLatest` spends neither.
 *
 * One arrival, one emission, and one input served per step: RxJS emits separately per source value,
 * so two packets landing together must leave as two packets, not be collapsed into one pairing.
 *
 * Until every input has delivered once there is nothing to pair with, so the arriving packet is
 * absorbed and nothing comes out — RxJS `combineLatest` is silent until its last source has produced.
 * It packs a tuple exactly like `zip` and transforms nothing, so a `map` behind it still does the
 * crafting, and an incoming tuple concatenates rather than nesting.
 */
export const combineLatestHandler: MachineHandler = {
  kind: 'combineLatest',
  step({ state, machine }) {
    if (machine.kind !== 'combineLatest') return [];
    const events: SimEvent[] = [];
    const { internal } = machine;
    const outputPort = singleOutput(machine.outputs);

    resetOnFreshSubscription(internal, state.subscribed);
    drainOutput(state, machine, events);
    if (!portHasRoom(outputPort)) return events;

    const portIndex = nextReadyPort(machine.inputs, internal.nextPort);
    if (portIndex === -1) return events;

    const inputPort = machine.inputs[portIndex];
    const arriving = state.packets[peekPort(inputPort)!]?.material;
    if (arriving !== undefined && !isMatter(arriving)) return events;

    const packetId = tryTakeFromPort(inputPort)!;
    if (arriving === undefined) return events;

    internal.latest[portIndex] = arriving;
    internal.nextPort = (portIndex + 1) % machine.inputs.length;

    if (!isComplete(internal.latest)) {
      delete state.packets[packetId];
      events.push({ type: 'packetDespawned', packetId });
      return events;
    }

    const tuple = flatten(internal.latest);
    state.packets[packetId].material = tuple;
    events.push({ type: 'packetTransformed', packetId, material: tuple });
    tryPushToPort(outputPort, packetId);
    return events;
  },
};
